/*
 * Keyboard control for the homepage product panel.
 *
 *   Enter / ArrowRight  – open the active slide's product panel
 *   ArrowLeft           – close it (same as the "Back" button)
 *   ArrowUp / ArrowDown – scroll inside the panel
 *   PageUp / PageDown   – scroll a screen at a time
 *
 * The hero's own initKeyboardNav() (fullscreen-product-hero.js) deliberately
 * bails while the panel is open, and it ignores anything inside
 * .fph-product-panel — so arrow keys did nothing once a panel was open.
 *
 * Above 990px the panel is not a single scroller: openPanel() builds two
 * class-less absolutely-positioned wraps (one around .fph-product-panel__intro,
 * one around .product-image-grid-section) and slides between them with
 * transforms, hijacking wheel events at the boundaries. Below 990px the panel
 * element itself scrolls normally.
 *
 * So: scroll the active wrap directly for normal movement, and at a boundary
 * dispatch a synthetic wheel event so the panel's existing handler performs the
 * section transition. That reuses its thresholds, locking and animation instead
 * of duplicating them.
 */
(function () {
  if (window.__fphPanelKeyboardInstalled) return;
  window.__fphPanelKeyboardInstalled = true;

  var LINE_STEP = 120;
  var EDGE_BOTTOM_SLACK = 30; // matches isAtWrapBottom()
  var EDGE_TOP_SLACK = 5; // matches isAtWrapTop()

  function openPanelEl() {
    var panel = document.querySelector('.fph-product-panel');
    return panel && panel.classList.contains('is-open') ? panel : null;
  }

  function wrapOf(panel, selector) {
    var el = panel.querySelector(selector);
    return el ? el.parentElement : null;
  }

  function directionFor(key) {
    if (key === 'ArrowDown' || key === 'PageDown') return 1;
    if (key === 'ArrowUp' || key === 'PageUp') return -1;
    return 0;
  }

  window.addEventListener('keydown', function (e) {
    if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;

    // Let popups layered above the panel keep their own behaviour.
    if (document.querySelector('.cart-popup.is-open, .nl-popup.is-open')) return;

    var target = e.target;
    if (
      target instanceof Element &&
      target.closest('input, textarea, select, [contenteditable]')
    ) {
      return;
    }

    var panel = openPanelEl();

    if (!panel) {
      // On a slide: Enter or ArrowRight opens that slide's product panel.
      if (e.key !== 'Enter' && e.key !== 'ArrowRight') return;

      // Only product slides have a media link; About/Contact don't.
      var slideLink = document.querySelector('.fph-slide.is-active .fph-slide__media-link');
      if (!slideLink) return;

      // Enter must not be stolen from a focused control that does its own thing
      // (ADD TO CART, footer links, nav). ArrowRight has no native action on
      // those, so it always opens the panel. Note that closing a panel returns
      // focus to the media link itself — that one must not count as "a control".
      if (e.key === 'Enter' && target instanceof Element) {
        var control = target.closest('a, button, [role="button"]');
        if (control && control !== slideLink) return;
      }

      e.preventDefault();
      slideLink.click(); // reuses the hero's own openPanel() handler
      return;
    }

    // ArrowLeft closes the panel, same as the Back button. Clicking the real
    // button keeps the history/popstate handling in the hero section intact.
    //
    // closePanel() in fullscreen-product-hero.js removes the panel and the cart
    // button but leaves its back button orphaned in <body>, so after the first
    // open/close there are several .fph-product-panel__back elements. The stale
    // ones carry display:none — take the last live one, not the first match.
    if (e.key === 'ArrowLeft') {
      var backBtn = Array.prototype.filter
        .call(document.querySelectorAll('.fph-product-panel__back'), function (b) {
          return b.style.display !== 'none';
        })
        .pop();
      if (!backBtn) return;
      e.preventDefault();
      backBtn.click();
      return;
    }

    var dir = directionFor(e.key);
    if (!dir) return;

    var introWrap = wrapOf(panel, '.fph-product-panel__intro');
    var gridWrap = wrapOf(panel, '.product-image-grid-section');
    var twoSection = !!(introWrap && gridWrap && introWrap !== gridWrap);

    // gridWrap is parked at translate3d(0,100%,0) while the intro is showing.
    var onIntro = twoSection && (gridWrap.style.transform || '').indexOf('100%') !== -1;
    var active = twoSection ? (onIntro ? introWrap : gridWrap) : panel;

    e.preventDefault();

    var atEdge =
      dir > 0
        ? active.scrollTop + active.clientHeight >= active.scrollHeight - EDGE_BOTTOM_SLACK
        : active.scrollTop <= EDGE_TOP_SLACK;

    // Only these two boundaries trigger a section change; anything else just scrolls.
    var crosses = twoSection && atEdge && ((dir > 0 && onIntro) || (dir < 0 && !onIntro));

    if (crosses) {
      active.dispatchEvent(
        new WheelEvent('wheel', {
          deltaY: dir * 200, // comfortably over the handler's 72px threshold
          deltaX: 0,
          deltaMode: 0,
          bubbles: true,
          cancelable: true,
        })
      );
      return;
    }

    var isPageKey = e.key === 'PageDown' || e.key === 'PageUp';
    var step = isPageKey ? Math.max(active.clientHeight * 0.9, LINE_STEP) : LINE_STEP;

    // Deliberately instant, not smooth: a smooth scrollBy is driven by
    // requestAnimationFrame and silently does nothing when the page isn't
    // painting (background tab, non-compositing view). A keypress must always
    // move the panel.
    active.scrollTop += dir * step;
  });
})();
